/**
 * Detailpaneel van één account in Budget: target + maandverdeling, reforecast vorig jaar,
 * invulvelden, bedrijven, open offertes en lopende projecten. Opslaan met undo.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Checkbox, CircularProgress, Drawer, FormControlLabel, IconButton, MenuItem, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import toast from 'react-hot-toast';
import { biedOngedaanAan } from '../../utils/ongedaan';
import { budgetApi } from './budgetApi';
import { MAANDNAMEN, alsInvoer, euro, gatTekst, leesBedrag, verdeelBedrag, verschil } from './budgetLogica';

const KOP = { fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 1 };
const DEMP = '#64748b';

function Sectie({ titel, children }) {
  return (
    <Box sx={{ py: 2, borderTop: '1px solid #eef1f4' }}>
      <Typography sx={KOP}>{titel}</Typography>
      {children}
    </Box>
  );
}

function Cijfer({ label, waarde, nadruk }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 12, color: DEMP }}>{label}</Typography>
      <Typography sx={{ fontSize: 16, fontWeight: nadruk ? 800 : 600, color: '#1f2937' }}>{waarde || '—'}</Typography>
    </Box>
  );
}

const leegFormulier = (d) => ({
  target: alsInvoer(d?.target),
  maanden: (d?.maanden || Array(12).fill(null)).map(alsInvoer),
  target_vorig: alsInvoer(d?.target_vorig),
  rf02: alsInvoer(d?.rf02),
  getekend: alsInvoer(d?.getekend),
  klant_type: d?.klant_type || '',
  recurrence: d?.recurrence || '',
  contractduur: d?.contractduur || '',
  only_netwerk: !!d?.only_netwerk,
  notitie: d?.notitie || '',
});

/** Formulier → API-body (euro's). Handmatige maanden → `maanden`, anders het jaartarget. */
export function naarBody(f, handmatig) {
  const bedrag = (v) => leesBedrag(v);
  const body = {
    target_vorig: bedrag(f.target_vorig),
    rf02: bedrag(f.rf02),
    getekend: bedrag(f.getekend),
    klant_type: f.klant_type || null,
    recurrence: f.recurrence || null,
    contractduur: f.contractduur || null,
    only_netwerk: f.only_netwerk,
    notitie: f.notitie.trim() || null,
  };
  if (handmatig) body.maanden = f.maanden.map((m) => bedrag(m) ?? 0);
  else body.target = bedrag(f.target);
  return body;
}

export default function BudgetPaneel({ sleutel, jaar, keuzes, magWijzigen, onClose, onGewijzigd }) {
  const [d, setD] = useState(null);
  const [f, setF] = useState(leegFormulier(null));
  const [handmatig, setHandmatig] = useState(false);
  const [bezig, setBezig] = useState(false);

  const laad = useCallback(async () => {
    try {
      const uit = await budgetApi.detail(sleutel, jaar);
      setD(uit);
      setF(leegFormulier(uit));
      setHandmatig(!!uit.maanden_handmatig);
    } catch (e) { toast.error(`Account laden mislukt: ${e.message}`); }
  }, [sleutel, jaar]);
  useEffect(() => { setD(null); laad(); }, [laad]);

  const zet = (k) => (e) => setF((x) => ({ ...x, [k]: e?.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e }));
  const zetMaand = (i) => (e) => {
    setHandmatig(true);
    setF((x) => {
      const maanden = x.maanden.map((m, j) => (j === i ? e.target.value : m));
      const som = maanden.reduce((s, m) => s + (leesBedrag(m) || 0), 0);
      return { ...x, maanden, target: alsInvoer(som) };
    });
  };
  const verdeelStandaard = () => {
    const t = leesBedrag(f.target) || 0;
    setHandmatig(false);
    setF((x) => ({ ...x, maanden: verdeelBedrag(t, d?.gewichten).map(alsInvoer) }));
  };
  const zetTarget = (e) => {
    const t = leesBedrag(e.target.value) || 0;
    setHandmatig(false);
    setF((x) => ({ ...x, target: e.target.value, maanden: verdeelBedrag(t, d?.gewichten).map(alsInvoer) }));
  };

  const origineel = useMemo(() => (d ? naarBody(leegFormulier(d), !!d.maanden_handmatig) : null), [d]);
  const body = naarBody(f, handmatig);
  const ongeldig = Object.entries(body).some(([k, v]) => (k === 'maanden' ? v.some((m) => Number.isNaN(m)) : Number.isNaN(v)));
  const wijziging = origineel ? verschil(origineel, body) : {};

  const opslaan = async () => {
    if (ongeldig) { toast.error('Een bedrag is niet goed gelezen — gebruik bijvoorbeeld 12500 of 12.500'); return; }
    if (!Object.keys(wijziging).length) return;
    setBezig(true);
    try {
      const uit = await budgetApi.bewaar(sleutel, { ...wijziging, versie: d.versie }, jaar);
      await laad();
      onGewijzigd?.();
      biedOngedaanAan(`${d.naam} opgeslagen`, async () => {
        await budgetApi.bewaar(sleutel, { ...uit.herstel, versie: uit.versie }, jaar);
        await laad();
        onGewijzigd?.();
      });
    } catch (e) {
      toast.error(e.message);
      if (e.status === 409) laad();
    } finally { setBezig(false); }
  };

  const gat = gatTekst(d?.gat);
  const ro = !magWijzigen;
  const veld = { size: 'small', fullWidth: true, disabled: ro };

  return (
    <Drawer anchor="right" open onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 620 } } }}>
      <Box sx={{ px: 3, pt: 2.5, pb: 1.5, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#1f2937' }}>{d?.naam || '…'}</Typography>
          <Typography sx={{ fontSize: 12.5, color: DEMP }}>
            {d ? `${d.aantal_bedrijven} ${d.aantal_bedrijven === 1 ? 'bedrijf' : 'bedrijven'}${d.status ? ` · ${d.status}` : ''}` : ''}
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Sluiten"><CloseIcon /></IconButton>
      </Box>

      {!d ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={28} /></Box>
      ) : (
        <Box sx={{ px: 3, pb: 12, overflowY: 'auto' }}>
          {d.telt_niet_mee && (
            <Typography sx={{ fontSize: 13, color: '#8a5a00', bgcolor: '#fff8e6', borderRadius: 1, px: 1.5, py: 1, mb: 2 }}>
              Dit budget telt niet mee: het bedrijf hoort nu bij een ander account (of de bron is weg). Zet de
              bedragen over naar dat account en maak ze hier leeg.
            </Typography>
          )}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, pb: 2 }}>
            <Cijfer label={`Realisatie ${d.jaar - 3}`} waarde={euro(d.realisatie[String(d.jaar - 3)])} />
            <Cijfer label={`Realisatie ${d.jaar - 2}`} waarde={euro(d.realisatie[String(d.jaar - 2)])} />
            <Cijfer label={`Forecast ${d.vorig_jaar}`} waarde={euro(d.forecast_vorig)} />
            <Cijfer label={`Target ${d.jaar}`} waarde={euro(d.target)} nadruk />
          </Box>

          <Sectie titel={`Target ${d.jaar}`}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5, mb: 1.5 }}>
              <TextField {...veld} label={`Target ${d.jaar}`} value={f.target} onChange={zetTarget} />
              <TextField {...veld} label="Al getekend" value={f.getekend} onChange={zet('getekend')}
                helperText="Retainers, lopende contracten" />
              <Box sx={{ pt: 0.5 }}>
                <Typography sx={{ fontSize: 12, color: DEMP }}>Gat (na gewogen pijplijn)</Typography>
                <Typography sx={{ fontSize: 16, fontWeight: 700, color: gat.open ? '#b42318' : '#1f2937' }}>{gat.tekst || '—'}</Typography>
                <Typography sx={{ fontSize: 11.5, color: '#94a3b8' }}>pijplijn {euro(d.pijplijn_gewogen) || '€ 0'} gewogen</Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(3, 1fr)', sm: 'repeat(6, 1fr)' }, gap: 1 }}>
              {MAANDNAMEN.map((m, i) => (
                <TextField key={m} {...veld} label={m} value={f.maanden[i]} onChange={zetMaand(i)}
                  inputProps={{ style: { fontSize: 13 } }} />
              ))}
            </Box>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
              <Typography sx={{ fontSize: 12, color: DEMP, flex: 1 }}>
                {handmatig ? 'Maanden met de hand ingevuld; het target is de som.' : 'Verdeeld volgens de standaard-maandverdeling.'}
              </Typography>
              {!ro && handmatig && (
                <Button size="small" onClick={verdeelStandaard} sx={{ textTransform: 'none' }}>Verdeel volgens standaard</Button>
              )}
            </Stack>
          </Sectie>

          <Sectie titel={`Reforecast ${d.vorig_jaar}`}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: 1.5, mb: 1.5 }}>
              <TextField {...veld} label={`Target ${d.vorig_jaar}`} value={f.target_vorig} onChange={zet('target_vorig')} />
              <TextField {...veld} label={`RF02 ${d.vorig_jaar}`} value={f.rf02} onChange={zet('rf02')} />
              <Cijfer label="Wat nog te halen" waarde={euro(d.nog_te_halen)} />
              <Cijfer label={`Forecast ${d.vorig_jaar}`} waarde={euro(d.forecast_vorig)} />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(3, 1fr)', sm: 'repeat(6, 1fr)' }, gap: 1 }}>
              {d.reforecast_maanden.map((m, i) => (
                <Box key={MAANDNAMEN[i]}>
                  <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>{MAANDNAMEN[i]}{m.soort === 'budget' ? ' · verwacht' : ''}</Typography>
                  <Typography sx={{ fontSize: 13, color: m.soort === 'budget' ? DEMP : '#1f2937', fontStyle: m.soort === 'budget' ? 'italic' : 'normal' }}>
                    {euro(m.bedrag)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Sectie>

          <Sectie titel="Hoe komen we daar">
            <TextField {...veld} multiline minRows={3} value={f.notitie} onChange={zet('notitie')}
              placeholder="Afspraken, kansen, wie pakt wat op" />
          </Sectie>

          <Sectie titel="Kenmerken">
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5 }}>
              {[['klant_type', 'Klant type'], ['recurrence', 'Recurrence'], ['contractduur', 'Contractduur (jaren)']].map(([k, label]) => (
                <TextField key={k} select {...veld} label={label} value={f[k]} onChange={zet(k)}>
                  <MenuItem value=""><em>—</em></MenuItem>
                  {(keuzes?.[k] || []).map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </TextField>
              ))}
            </Box>
            <FormControlLabel sx={{ mt: 1 }} disabled={ro}
              control={<Checkbox size="small" checked={f.only_netwerk} onChange={zet('only_netwerk')} />}
              label={<Typography sx={{ fontSize: 14 }}>Klant verkregen via het Only-netwerk</Typography>} />
          </Sectie>

          {d.bedrijven.length > 0 && (
            <Sectie titel="Bedrijven (Gripp)">
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell sx={{ color: DEMP }}>Bedrijf</TableCell>
                  {[d.jaar - 3, d.jaar - 2, d.jaar - 1].map((y) => <TableCell key={y} align="right" sx={{ color: DEMP }}>{y}</TableCell>)}
                </TableRow></TableHead>
                <TableBody>
                  {d.bedrijven.map((b) => (
                    <TableRow key={b.company_id}>
                      <TableCell>{b.naam}</TableCell>
                      {[d.jaar - 3, d.jaar - 2, d.jaar - 1].map((y) => <TableCell key={y} align="right">{euro(b.realisatie[String(y)])}</TableCell>)}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Typography sx={{ fontSize: 11.5, color: '#94a3b8', mt: 0.5 }}>{d.jaar - 1}: gefactureerd tot nu toe.</Typography>
            </Sectie>
          )}

          <Sectie titel={`Open offertes (${d.offertes.length})`}>
            {d.offertes.length === 0 ? <Typography sx={{ fontSize: 13, color: '#94a3b8' }}>Geen open offertes.</Typography> : (
              <Table size="small">
                <TableHead><TableRow>
                  {['Offerte', 'Fase', 'Bedrag', 'Gewogen'].map((h, i) => <TableCell key={h} align={i > 1 ? 'right' : 'left'} sx={{ color: DEMP }}>{h}</TableCell>)}
                </TableRow></TableHead>
                <TableBody>
                  {d.offertes.map((o) => (
                    <TableRow key={o.gripp_id}>
                      <TableCell sx={{ maxWidth: 240 }}>{o.nummer ? `${o.nummer} · ` : ''}{o.onderwerp}</TableCell>
                      <TableCell sx={{ color: DEMP, whiteSpace: 'nowrap' }}>{o.fase || '—'}</TableCell>
                      <TableCell align="right">{euro(o.bedrag)}</TableCell>
                      <TableCell align="right">{euro(o.gewogen)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <Typography sx={{ fontSize: 11.5, color: '#94a3b8', mt: 0.5 }}>Open offertes van de laatste 12 maanden × kans uit de fase; niet naar jaar verdeeld.</Typography>
          </Sectie>

          <Sectie titel={`Lopend werk (${d.projecten.length})`}>
            {d.projecten.length === 0 ? <Typography sx={{ fontSize: 13, color: '#94a3b8' }}>Geen lopende projecten.</Typography> : (
              <Table size="small">
                <TableBody>
                  {d.projecten.map((p) => (
                    <TableRow key={p.gripp_id}>
                      <TableCell sx={{ maxWidth: 280 }}>{p.nummer ? `${p.nummer} · ` : ''}{p.naam}</TableCell>
                      <TableCell sx={{ color: DEMP, whiteSpace: 'nowrap' }}>{p.status || ''}</TableCell>
                      <TableCell align="right">{euro(p.waarde)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <Typography sx={{ fontSize: 11.5, color: '#94a3b8', mt: 0.5 }}>Projectwaarde uit Gripp, als hulp bij "al getekend".</Typography>
          </Sectie>

          {d.log?.length > 0 && (
            <Sectie titel="Wijzigingen">
              {d.log.slice(0, 5).map((l) => (
                <Typography key={`${l.op}-${l.door}`} sx={{ fontSize: 12.5, color: DEMP }}>
                  {new Date(`${l.op}Z`).toLocaleString('nl-NL', { dateStyle: 'medium', timeStyle: 'short' })} · {l.door} · {l.velden.join(', ')}
                </Typography>
              ))}
            </Sectie>
          )}
        </Box>
      )}

      {d && magWijzigen && (
        <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, px: 3, py: 1.5, bgcolor: '#fff', borderTop: '1px solid #eef1f4',
          display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: 12, color: '#94a3b8', flex: 1 }}>
            {d.gewijzigd_door ? `Laatst: ${d.gewijzigd_door}` : 'Nog niets ingevuld'}
          </Typography>
          <Button onClick={() => { setF(leegFormulier(d)); setHandmatig(!!d.maanden_handmatig); }} disabled={bezig || !Object.keys(wijziging).length} sx={{ textTransform: 'none' }}>Herstel</Button>
          <Button variant="contained" disableElevation onClick={opslaan} disabled={bezig || !Object.keys(wijziging).length}
            sx={{ textTransform: 'none', fontWeight: 700 }}>Opslaan</Button>
        </Box>
      )}
    </Drawer>
  );
}
